import "./index.css";
import { formatDuration, useTabAudioRecorder } from "./useTabAudioRecorder";

function sourceLabel(sources: { tab: boolean; mic: boolean }): string {
  if (sources.tab && sources.mic) return "Tab + microphone";
  if (sources.tab) return "Tab audio";
  if (sources.mic) return "Microphone";
  return "Audio";
}

export function App() {
  const {
    isRecording,
    recordings,
    error,
    warning,
    elapsedMs,
    includeMicrophone,
    setIncludeMicrophone,
    startRecording,
    stopRecording,
    removeRecording,
    transcribeRecording,
    clearError,
    clearWarning,
  } = useTabAudioRecorder();

  return (
    <div className="app">
      <nav className="app-nav">
        <a href="/" className="app-nav-active">
          Recorder
        </a>
      </nav>

      <header className="app-header">
        <h1>Aufnehmen</h1>
        <p className="subtitle">
          Record tab audio and microphone. We process it live and show the transcript.
        </p>
      </header>

      <section className="recorder-panel" aria-live="polite">
        {error && (
          <div className="banner banner-error" role="alert">
            <span>{error}</span>
            <button type="button" className="banner-dismiss" onClick={clearError}>
              Dismiss
            </button>
          </div>
        )}
        {warning && (
          <div className="banner banner-warning" role="status">
            <span>{warning}</span>
            <button type="button" className="banner-dismiss" onClick={clearWarning}>
              Dismiss
            </button>
          </div>
        )}

        <label className="mic-toggle">
          <input
            type="checkbox"
            checked={includeMicrophone}
            disabled={isRecording}
            onChange={(e) => setIncludeMicrophone(e.target.checked)}
          />
          <span>Include microphone (mixed with tab audio)</span>
        </label>

        <div className="recorder-controls">
          {!isRecording ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void startRecording()}
            >
              Start recording
            </button>
          ) : (
            <>
              <span className="recording-indicator">
                <span className="recording-dot" aria-hidden />
                Recording {formatDuration(elapsedMs)}
                <span className="recording-sources">
                  {includeMicrophone ? "Tab + mic" : "Tab"}
                </span>
              </span>
              <button type="button" className="btn btn-danger" onClick={stopRecording}>
                Stop
              </button>
            </>
          )}
        </div>

        <ol className="hints">
          <li>Click <strong>Start recording</strong> and choose a <strong>Chrome Tab</strong>.</li>
          <li>Turn on <strong>Also share tab audio</strong> in the share dialog.</li>
          <li>
            {includeMicrophone
              ? "Allow microphone access when prompted — tab and mic are recorded as one file."
              : "Only tab audio is captured (microphone is off)."}
          </li>
          <li>We process it live and show the transcript below.</li>
        </ol>
      </section>

      <section className="recordings-section">
        <h2>Recordings</h2>
        {recordings.length === 0 ? (
          <p className="empty">No recordings yet.</p>
        ) : (
          <ul className="recording-list">
            {recordings.map((r) => (
              <li key={r.id} className="recording-item">
                <div className="recording-meta">
                  <time dateTime={r.createdAt.toISOString()}>
                    {r.createdAt.toLocaleString()}
                  </time>
                  <span className="source-badge">{sourceLabel(r.sources)}</span>
                  <span className="duration">{formatDuration(r.durationMs)}</span>
                </div>
                {r.url ? (
                  <audio controls src={r.url} preload="metadata" />
                ) : (
                  <p className="transcription-meta">Recording in progress…</p>
                )}

                <div className="transcription-card">
                  <div className="transcription-header">
                    <strong>Transcript</strong>
                    <span className={`transcription-status transcription-${r.transcriptionStatus}`}>
                      {r.transcriptionStatus === "idle" && "Queued"}
                      {r.transcriptionStatus === "processing" && "Transcribing…"}
                      {r.transcriptionStatus === "done" && "Done"}
                      {r.transcriptionStatus === "error" && "Error"}
                    </span>
                  </div>

                  {r.transcriptionStatus === "done" && (
                    <pre className="transcript-text">{r.transcript}</pre>
                  )}

                  {r.transcriptionStatus === "processing" && (
                    <p className="transcription-meta">
                      Processing live…
                    </p>
                  )}

                  {r.transcriptionStatus === "error" && (
                    <p className="transcription-error-message">{r.transcriptionError}</p>
                  )}
                </div>

                <div className="recording-actions">
                  {r.url && (
                    <a
                      className="btn btn-ghost"
                      href={r.url}
                      download={`aufnehmen-${r.createdAt.toISOString().slice(0, 19).replace(/:/g, "-")}.webm`}
                    >
                      Download
                    </a>
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={r.transcriptionStatus === "processing" || !r.url}
                    onClick={() => void transcribeRecording(r)}
                  >
                    Retry transcript
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-danger-text"
                    onClick={() => removeRecording(r.id)}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default App;
